import { useCallback, useEffect, useRef, useState } from 'react';
import { liveStreams, type LiveStream as MockLiveStream } from '@/mocks/lives';
import { fetchLiveStreams, type LiveStream as DbLiveStream, type LivePlatform } from '@/lib/lives';
import { formatCount } from '@/lib/media';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import ProposeLiveModal from './ProposeLiveModal';

const PLATFORM_ICON: Record<LivePlatform, string> = {
  youtube: 'ri-youtube-fill text-accent-400',
  tiktok: 'ri-tiktok-fill text-foreground-50',
  instagram: 'ri-instagram-fill text-accent-400',
  linkedin: 'ri-linkedin-fill text-primary-400',
  google: 'ri-google-fill text-secondary-300',
};

const PLATFORM_LABEL: Record<LivePlatform, string> = {
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  google: 'Google',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseProducts(products: string | null): string[] {
  if (!products) return [];
  return products
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
    .slice(0, 5);
}

interface RenderStream {
  id: string;
  title: string;
  host: string;
  handle: string;
  category: string;
  thumbnail_url: string | null;
  viewers: number;
  platform: LivePlatform;
  status: 'live' | 'upcoming';
  scheduled_at: string | null;
  link_url: string;
  products: string | null;
}

function LiveSection() {
  const [proposed, setProposed] = useState<DbLiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [now, setNow] = useState<number>(Date.now());
  const { copy } = useCopyToClipboard();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const shareTimerRef = useRef<number | null>(null);

  const loadProposed = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLiveStreams();
      setProposed(data);
    } catch {
      setProposed([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProposed();
  }, [loadProposed]);

  // Rafraîchit l'horloge toutes les 30 s pour basculer automatiquement en EN DIRECT
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const shareLive = (stream: RenderStream) => {
    const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
    const prefix = basePath ? `/${basePath}` : '';
    const url = `${window.location.origin}${prefix}/media?tab=live`;
    copy(`${stream.title} — ${url}`);
    setCopiedId(stream.id);
    if (shareTimerRef.current) window.clearTimeout(shareTimerRef.current);
    shareTimerRef.current = window.setTimeout(() => setCopiedId(null), 2000);
  };

  const allStreams: RenderStream[] = [
    ...liveStreams.map((l: MockLiveStream) => ({
      id: l.id,
      title: l.title,
      host: l.host,
      handle: l.handle,
      category: l.category,
      thumbnail_url: l.thumbnail_url,
      viewers: l.viewers,
      platform: l.platform as LivePlatform,
      status: l.status,
      scheduled_at: l.scheduled_at,
      link_url: l.link_url,
      products: l.products,
    })),
    ...proposed.map((p) => ({
      id: p.id,
      title: p.title,
      host: p.host,
      handle: p.handle || '',
      category: p.category,
      thumbnail_url: null,
      viewers: 0,
      platform: p.platform,
      status: p.status,
      scheduled_at: p.scheduled_at,
      link_url: p.link_url,
      products: p.products,
    })),
  ];

  // Un live dure 3 heures à partir de son heure de début
  const LIVE_DURATION_MS = 3 * 60 * 60 * 1000;

  const resolveStatus = (stream: RenderStream): 'live' | 'upcoming' | 'ended' => {
    if (!stream.scheduled_at) {
      return stream.status === 'live' ? 'live' : 'upcoming';
    }
    const start = new Date(stream.scheduled_at).getTime();
    const end = start + LIVE_DURATION_MS;
    if (now >= start && now <= end) return 'live';
    if (now > end) return 'ended';
    return 'upcoming';
  };

  const live: RenderStream[] = [];
  const upcoming: RenderStream[] = [];

  allStreams.forEach((stream) => {
    const state = resolveStatus(stream);
    if (state === 'live') {
      live.push({ ...stream, status: 'live' });
    } else if (state === 'upcoming') {
      upcoming.push({ ...stream, status: 'upcoming' });
    }
  });

  upcoming.sort((a, b) => {
    const da = a.scheduled_at ? new Date(a.scheduled_at).getTime() : Infinity;
    const db = b.scheduled_at ? new Date(b.scheduled_at).getTime() : Infinity;
    return da - db;
  });

  const renderCard = (stream: RenderStream) => {
    const productTags = parseProducts(stream.products);
    return (
      <article
        key={stream.id}
        className="group relative rounded-xl overflow-hidden border border-background-200/70 bg-background-50 hover:border-background-300/70 transition-colors flex flex-col"
      >
        <a
          href={stream.link_url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="relative block aspect-video overflow-hidden"
        >
          {stream.thumbnail_url ? (
            <img
              src={stream.thumbnail_url}
              alt={stream.title}
              title={`${stream.host} — ${stream.title}`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-background-800 to-background-950">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/15">
                <i className="ri-live-line text-2xl text-primary-400" />
              </div>
              <span className="text-xs text-foreground-500 font-medium">Live programmé</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

          {stream.status === 'live' ? (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-foreground-950 text-xs font-bold">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-foreground-950 opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-foreground-950" />
              </span>
              EN DIRECT
            </span>
          ) : (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-950/60 backdrop-blur-sm text-foreground-50 text-xs font-semibold">
              <i className="ri-calendar-line" />
              {stream.scheduled_at ? formatDate(stream.scheduled_at) : 'Bientôt'}
            </span>
          )}

          <span className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-background-950/50 backdrop-blur-sm">
            <i className={`${PLATFORM_ICON[stream.platform]} text-base`} />
          </span>

          <div className="absolute bottom-3 left-3 right-3">
            <h3 className="font-heading font-semibold text-base text-foreground-50 leading-snug line-clamp-2">
              {stream.title}
            </h3>
          </div>
        </a>

        <div className="p-4 flex flex-col gap-3 flex-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 font-bold text-sm shrink-0">
                {stream.host.charAt(0)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-50 truncate">{stream.host}</p>
                <p className="text-xs text-foreground-400 truncate">
                  {stream.handle} · {PLATFORM_LABEL[stream.platform]}
                </p>
              </div>
            </div>
            {stream.status === 'live' && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-50 shrink-0">
                <i className="ri-eye-line text-accent-400" />
                {formatCount(stream.viewers)} spectateurs
              </span>
            )}
          </div>

          {productTags.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ri-shopping-bag-3-line text-accent-400 text-sm" />
                <span className="text-xs font-semibold text-foreground-300">Produits présentés</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {productTags.map((p) => (
                  <span
                    key={p}
                    className="px-2.5 py-1 rounded-md bg-background-200/40 border border-background-300/40 text-xs text-foreground-300 whitespace-nowrap"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-3 border-t border-background-200/70 mt-auto">
            <a
              href={stream.link_url}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-eye-line" />
              Regarder
            </a>
            <button
              type="button"
              onClick={() => shareLive(stream)}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-background-300/60 text-foreground-300 font-semibold text-sm hover:text-primary-400 hover:border-primary-400/40 transition-colors whitespace-nowrap"
              aria-label={`Partager ${stream.title}`}
              title="Copier le lien du live"
            >
              <i className={copiedId === stream.id ? 'ri-check-line text-accent-400' : 'ri-share-line'} />
              {copiedId === stream.id ? 'Copié !' : 'Partager'}
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div>
      {/* En direct */}
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2.5 h-2.5 rounded-full bg-accent-500 animate-pulse" />
        <h2 className="font-heading font-semibold text-lg text-foreground-50">En direct maintenant</h2>
        <span className="text-sm text-foreground-400">({live.length})</span>
      </div>

      {live.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {live.map(renderCard)}
        </div>
      ) : (
        <p className="text-foreground-400 mb-10">Aucun live en ce moment. Revenez bientôt !</p>
      )}

      {/* À venir */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <i className="ri-calendar-todo-line text-accent-400" />
          <h2 className="font-heading font-semibold text-lg text-foreground-50">Prochains lives</h2>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Proposer un live
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
        </div>
      ) : upcoming.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {upcoming.map(renderCard)}
        </div>
      ) : (
        <p className="text-foreground-400">Aucun live programmé pour le moment.</p>
      )}

      <ProposeLiveModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onProposed={loadProposed}
      />
    </div>
  );
}

export default LiveSection;